import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { AlertTriangle, ChevronDown } from 'lucide-react';
import ConsiderationBreakdownBar from './ConsiderationBreakdownBar';
import TaxEfficiencyDonut from './TaxEfficiencyDonut';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);

const formatCurrencyCompact = (value) => {
  const n = Number(value || 0);
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
  return `$${n.toLocaleString()}`;
};

const formatPercentDecimal = (decimal) => `${(Number(decimal || 0) * 100).toFixed(0)}%`;
const formatPercentInteger = (value) => `${Number(value || 0).toFixed(0)}%`;

const PROVINCE_LABEL = {
  ON: 'Ontario',
  QC: 'Quebec',
  BC: 'British Columbia',
  AB: 'Alberta',
  MB: 'Manitoba',
  SK: 'Saskatchewan',
  NS: 'Nova Scotia',
  NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador',
  PE: 'Prince Edward Island',
};

const getRiskColor = (decimal) => {
  if (decimal >= 0.3) return 'text-red-600 dark:text-red-500';
  if (decimal >= 0.2) return 'text-amber-600 dark:text-amber-500';
  return 'text-green-600 dark:text-green-500';
};

const InputCell = ({ label, value }) => (
  <div>
    <div className="text-[11px] text-[hsl(var(--muted-foreground))] mb-1">{label}</div>
    <div className="text-[13px] text-[hsl(var(--foreground))]">{value}</div>
  </div>
);

InputCell.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

// Inline cumulative-payout timeline matches the actual data shape
// (results.timeline = [{ period, cumulative }]). The Polymet PaymentTimeline
// expected discrete events { year, source, amount } that aren't in our data
// model, so we render the cumulative running-total view instead.
const CumulativePayoutTimeline = ({ timeline }) => {
  if (!timeline || timeline.length === 0) {
    return (
      <p className="text-[13px] text-[hsl(var(--muted-foreground))]">
        Payment timeline not modeled in this snapshot.
      </p>
    );
  }

  return (
    <div>
      <div className="relative">
        <div className="absolute top-6 left-0 right-0 h-[2px] bg-[hsl(var(--border))]" />
        <div className="relative flex justify-between">
          {timeline.map((point, idx) => (
            <div
              // Period strings ('Close', 'Y1', 'Y2') are unique within a
              // single timeline so they're a stable React key.
              // eslint-disable-next-line react/no-array-index-key
              key={point.period || `idx-${idx}`}
              className="flex flex-col items-center"
              style={{ flex: 1 }}
            >
              <div
                className={`w-3 h-3 rounded-full border-2 border-[hsl(var(--border))] z-10 ${
                  idx === 0
                    ? 'bg-[hsl(var(--succeedo-orange))]'
                    : 'bg-[hsl(var(--card))]'
                }`}
              />
              <div className="mt-2 text-[11px] text-[hsl(var(--muted-foreground))] font-mono uppercase">
                {point.period}
              </div>
              <div className="mt-1 text-[13px] text-[hsl(var(--foreground))] font-medium">
                {formatCurrencyCompact(point.cumulative)}
              </div>
            </div>
          ))}
        </div>
      </div>
      <p className="mt-4 text-[11px] text-[hsl(var(--muted-foreground))]/70">
        Running total of after-tax proceeds at each milestone.
      </p>
    </div>
  );
};

CumulativePayoutTimeline.propTypes = {
  timeline: PropTypes.arrayOf(
    PropTypes.shape({
      period: PropTypes.string.isRequired,
      cumulative: PropTypes.number.isRequired,
    }),
  ),
};

CumulativePayoutTimeline.defaultProps = {
  timeline: [],
};

const DealStructureSnapshotExpertView = ({ snapshot }) => {
  const [inputsExpanded, setInputsExpanded] = useState(false);

  const inputs = snapshot?.inputs || {};
  const results = snapshot?.results || {};

  // Real shape from DealStructureResults.buildParams + the SmbDealStructureService output:
  //   inputs (camelCase): askingPrice, downPayment, sellerNoteAmount,
  //     repaymentTermYears, interestRate, earnoutAmount, earnoutDurationYears,
  //     province, consultingAnnualFee, consultingDurationYears, annualRevenue,
  //     annualExpenses, revenueGrowthRate, expenseGrowthRate, cashFlowLength.
  //   results: { asking_price, cash_at_closing, after_tax_proceeds,
  //     tax: { province, marginal_rate, effective_tax_rate, lcge_applied,
  //            capital_gains_tax, interest_income_tax, consulting_income_tax,
  //            total_tax },
  //     timeline: [{ period, cumulative }],
  //     seller_note: { term_years, rate, monthly_payment, total_interest },
  //     earnout: { ... },
  //     consulting: { tax, net_income, annual_fee } }.
  //
  // Polymet's brief mocked an enterprise-style M&A structure with fields the
  // small-business model doesn't capture (working capital target, structure
  // type enum, buyer type enum, escrow/holdback, sensitivity scenarios,
  // separate capital-gains and ordinary-income tax rates). Sections that
  // depend on those are dropped or replaced with available data.

  const askingPrice = Number(inputs.askingPrice || results.asking_price || 0);
  const downPayment = Number(inputs.downPayment || 0);
  const sellerNoteAmount = Number(inputs.sellerNoteAmount || 0);
  const earnoutAmount = Number(inputs.earnoutAmount || 0);
  const earnoutDurationYears = Number(inputs.earnoutDurationYears || 0);
  const repaymentTermYears = Number(inputs.repaymentTermYears || 0);
  const interestRate = Number(inputs.interestRate || 0);
  const consultingAnnualFee = Number(inputs.consultingAnnualFee || 0);
  const consultingDurationYears = Number(inputs.consultingDurationYears || 0);

  const cashAtClosing = Number(results.cash_at_closing || downPayment || 0);
  const afterTaxProceeds = Number(results.after_tax_proceeds || 0);

  const tax = results.tax || {};
  const timeline = results.timeline || [];
  const sellerNote = results.seller_note || {};
  const consulting = results.consulting || {};

  const safeGross = askingPrice > 0 ? askingPrice : 1;
  const cashAtClosingPct = (cashAtClosing / safeGross) * 100;

  // Effective tax rate: stored as a decimal in tax.effective_tax_rate (e.g.,
  // 0.27 for 27%). Use that directly.
  const effectiveTaxRate = Number(tax.effective_tax_rate || 0);

  // Risk: VTB principal + earnout total (deferred consideration) divided by
  // gross. The owner doesn't get this money guaranteed — earnout is
  // contingent on performance, VTB on buyer payment.
  const vtbAtRiskPct = sellerNoteAmount / safeGross;
  const earnoutAtRiskPct = earnoutAmount / safeGross;
  const totalAtRiskPct = (sellerNoteAmount + earnoutAmount) / safeGross;

  // Tax breakdown adapter: the donut expects { capital_gains_amount,
  // ordinary_income_amount, lcge_offset, total_tax }. Map from the actual
  // tax shape — interest income + consulting income tax are both "ordinary".
  const taxBreakdown = {
    capital_gains_amount: Number(tax.capital_gains_tax || 0),
    ordinary_income_amount:
      Number(tax.interest_income_tax || 0) + Number(tax.consulting_income_tax || 0),
    lcge_offset: Number(tax.lcge_applied || 0),
    total_tax: Number(tax.total_tax || 0),
  };
  const hasTaxData = taxBreakdown.total_tax > 0 || taxBreakdown.capital_gains_amount > 0;

  const province = tax.province || inputs.province;
  const provinceLabel = province ? PROVINCE_LABEL[province] || province : null;

  const hasEarnout = earnoutAmount > 0;
  const hasSellerNote = sellerNoteAmount > 0;
  const hasConsulting = consultingAnnualFee > 0;
  const earnoutHighRisk = earnoutAtRiskPct > 0.25 || earnoutDurationYears > 3;

  return (
    <div className="space-y-8">
      {/* Section A — Headline Panel */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-6">
          <div className="font-mono text-[11px] uppercase text-[hsl(var(--muted-foreground))] tracking-wide mb-3">
            TAKE-HOME (AFTER-TAX)
          </div>
          <div className="text-[32px] font-light text-[hsl(var(--succeedo-orange))] mb-2">
            {formatCurrency(afterTaxProceeds)}
          </div>
          <div className="text-[13px] text-[hsl(var(--muted-foreground))]">
            vs. Gross consideration: {formatCurrency(askingPrice)}
            {' — effective rate '}
            {formatPercentDecimal(effectiveTaxRate)}
          </div>
        </div>

        <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-6">
          <div className="font-mono text-[11px] uppercase text-[hsl(var(--muted-foreground))] tracking-wide mb-3">
            CASH AT CLOSING
          </div>
          <div className="text-[32px] font-light text-[hsl(var(--foreground))] mb-2">
            {formatCurrency(cashAtClosing)}
          </div>
          <div className="text-[13px] text-[hsl(var(--muted-foreground))]">
            {formatPercentInteger(cashAtClosingPct)} of gross consideration
          </div>
        </div>
      </div>

      {/* Section B — Province pill (only meaningful badge available) */}
      {provinceLabel && (
        <div className="flex flex-wrap gap-3">
          <span className="font-mono text-[11px] uppercase tracking-wide px-3 py-1 rounded-full border border-[hsl(var(--border))] text-[hsl(var(--foreground))]">
            JURISDICTION: {provinceLabel}
          </span>
        </div>
      )}

      {/* Section C — Consideration Breakdown */}
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-6">
        <h2 className="font-serif text-[28px] font-light text-[hsl(var(--foreground))] mb-6">Consideration Breakdown</h2>
        <ConsiderationBreakdownBar
          grossConsideration={askingPrice}
          cashAtClosing={cashAtClosing}
          vendorTakeback={sellerNoteAmount}
          earnout={earnoutAmount}
          escrow={0}
        />
      </div>

      {/* Section D — Payment Timeline */}
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-6">
        <h2 className="font-serif text-[28px] font-light text-[hsl(var(--foreground))] mb-6">Payment Timeline</h2>
        <CumulativePayoutTimeline timeline={timeline} />
      </div>

      {/* Section E — Earnout Terms (conditional) */}
      {hasEarnout && (
        <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-6">
          <div className="flex items-start justify-between mb-4">
            <h2 className="font-serif text-[28px] font-light text-[hsl(var(--foreground))]">Earnout Terms</h2>
            {earnoutHighRisk && (
              <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase px-2 py-0.5 rounded-full border text-amber-600 dark:text-amber-500 border-amber-600 dark:border-amber-500">
                <AlertTriangle className="w-3 h-3" />
                High Risk
              </span>
            )}
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))] mb-1">Total Earnout</div>
                <div className="text-[15px] text-[hsl(var(--foreground))] font-medium">
                  {formatCurrency(earnoutAmount)}
                </div>
              </div>
              <div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))] mb-1">Duration</div>
                <div className="text-[15px] text-[hsl(var(--foreground))] font-medium">
                  {earnoutDurationYears} {earnoutDurationYears === 1 ? 'year' : 'years'}
                </div>
              </div>
            </div>

            <div>
              <div className="text-[11px] text-[hsl(var(--muted-foreground))] mb-1">% at Risk</div>
              <div className={`text-[15px] font-semibold ${getRiskColor(earnoutAtRiskPct)}`}>
                {formatPercentDecimal(earnoutAtRiskPct)}
              </div>
            </div>

            <p className="text-[11px] text-[hsl(var(--muted-foreground))]/70">
              Earnout is contingent on post-close business performance. Milestone-level breakdown
              is not modeled in this snapshot.
            </p>
          </div>
        </div>
      )}

      {/* Section F — Vendor Takeback (conditional) */}
      {hasSellerNote && (
        <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-6">
          <h2 className="font-serif text-[28px] font-light text-[hsl(var(--foreground))] mb-4">Vendor Takeback</h2>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <div className="text-[11px] text-[hsl(var(--muted-foreground))] mb-1">Amount</div>
              <div className="text-[15px] text-[hsl(var(--foreground))] font-medium">
                {formatCurrency(sellerNoteAmount)}
              </div>
            </div>
            <div>
              <div className="text-[11px] text-[hsl(var(--muted-foreground))] mb-1">Term</div>
              <div className="text-[15px] text-[hsl(var(--foreground))] font-medium">
                {repaymentTermYears} {repaymentTermYears === 1 ? 'year' : 'years'}
              </div>
            </div>
            <div>
              <div className="text-[11px] text-[hsl(var(--muted-foreground))] mb-1">Interest Rate</div>
              <div className="text-[15px] text-[hsl(var(--foreground))] font-medium">
                {formatPercentDecimal(interestRate)}
              </div>
            </div>
            {sellerNote.monthly_payment > 0 && (
              <div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))] mb-1">Monthly Payment</div>
                <div className="text-[15px] text-[hsl(var(--foreground))] font-medium">
                  {formatCurrency(sellerNote.monthly_payment)}
                </div>
              </div>
            )}
          </div>

          {sellerNote.total_interest > 0 && (
            <div className="pt-4 border-t border-[hsl(var(--border))]">
              <div className="flex items-center justify-between">
                <span className="text-[13px] text-[hsl(var(--muted-foreground))]">Total interest over term</span>
                <span className="text-[13px] text-[hsl(var(--foreground))] font-medium">
                  {formatCurrency(sellerNote.total_interest)}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Section G — Consulting Agreement (conditional, Succeedo-specific
           — not in Polymet's brief but real here) */}
      {hasConsulting && (
        <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-6">
          <h2 className="font-serif text-[28px] font-light text-[hsl(var(--foreground))] mb-4">Consulting Agreement</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-[11px] text-[hsl(var(--muted-foreground))] mb-1">Annual Fee</div>
              <div className="text-[15px] text-[hsl(var(--foreground))] font-medium">
                {formatCurrency(consultingAnnualFee)}
              </div>
            </div>
            <div>
              <div className="text-[11px] text-[hsl(var(--muted-foreground))] mb-1">Duration</div>
              <div className="text-[15px] text-[hsl(var(--foreground))] font-medium">
                {consultingDurationYears} {consultingDurationYears === 1 ? 'year' : 'years'}
              </div>
            </div>
            {consulting.net_income > 0 && (
              <div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))] mb-1">Total Net Income</div>
                <div className="text-[15px] text-[hsl(var(--foreground))] font-medium">
                  {formatCurrency(consulting.net_income)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Section H — Tax Efficiency (conditional — only when tax data present) */}
      {hasTaxData && (
        <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-6">
          <h2 className="font-serif text-[28px] font-light text-[hsl(var(--foreground))] mb-6">Tax Efficiency</h2>
          <TaxEfficiencyDonut breakdown={taxBreakdown} grossConsideration={askingPrice} />
          {tax.marginal_rate > 0 && (
            <div className="mt-4 pt-4 border-t border-[hsl(var(--border))] flex items-center justify-between">
              <span className="text-[13px] text-[hsl(var(--muted-foreground))]">Marginal rate</span>
              <span className="text-[13px] text-[hsl(var(--foreground))] font-medium">
                {formatPercentDecimal(tax.marginal_rate)}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Section I — Risk Summary */}
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-6">
        <h2 className="font-serif text-[28px] font-light text-[hsl(var(--foreground))] mb-4">Risk Summary</h2>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <div className="text-[11px] text-[hsl(var(--muted-foreground))] mb-1">Total at Risk</div>
            <div className={`text-[20px] font-semibold ${getRiskColor(totalAtRiskPct)}`}>
              {formatPercentDecimal(totalAtRiskPct)}
            </div>
          </div>
          <div>
            <div className="text-[11px] text-[hsl(var(--muted-foreground))] mb-1">Earnout at Risk</div>
            <div className={`text-[20px] font-semibold ${getRiskColor(earnoutAtRiskPct)}`}>
              {formatPercentDecimal(earnoutAtRiskPct)}
            </div>
          </div>
          <div>
            <div className="text-[11px] text-[hsl(var(--muted-foreground))] mb-1">VTB at Risk</div>
            <div className={`text-[20px] font-semibold ${getRiskColor(vtbAtRiskPct)}`}>
              {formatPercentDecimal(vtbAtRiskPct)}
            </div>
          </div>
        </div>

        <p className="mt-4 text-[11px] text-[hsl(var(--muted-foreground))]/70">
          % of gross consideration tied up in deferred / contingent payments.
        </p>
      </div>

      {/* Section J — Inputs Reference */}
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px]">
        <button
          type="button"
          onClick={() => setInputsExpanded(!inputsExpanded)}
          className="w-full flex items-center justify-between p-6 text-left"
        >
          <h2 className="font-serif text-[28px] font-light text-[hsl(var(--foreground))]">Inputs Reference</h2>
          <ChevronDown
            className={`w-5 h-5 text-[hsl(var(--muted-foreground))] transition-transform ${inputsExpanded ? 'rotate-180' : ''}`}
          />
        </button>

        {inputsExpanded && (
          <div className="px-6 pb-6 border-t border-[hsl(var(--border))] pt-6">
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              <InputCell label="Asking Price" value={formatCurrency(askingPrice)} />
              <InputCell label="Cash at Closing" value={formatCurrency(cashAtClosing)} />
              {hasSellerNote && (
                <>
                  <InputCell label="Seller Note" value={formatCurrency(sellerNoteAmount)} />
                  <InputCell label="Repayment Term" value={`${repaymentTermYears} years`} />
                  <InputCell label="Interest Rate" value={formatPercentDecimal(interestRate)} />
                </>
              )}
              {hasEarnout && (
                <>
                  <InputCell label="Earnout" value={formatCurrency(earnoutAmount)} />
                  <InputCell label="Earnout Duration" value={`${earnoutDurationYears} years`} />
                </>
              )}
              {hasConsulting && (
                <>
                  <InputCell label="Consulting Annual Fee" value={formatCurrency(consultingAnnualFee)} />
                  <InputCell label="Consulting Duration" value={`${consultingDurationYears} years`} />
                </>
              )}
              {provinceLabel && <InputCell label="Province" value={provinceLabel} />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

DealStructureSnapshotExpertView.propTypes = {
  snapshot: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
    preview_metrics: PropTypes.shape({
      primary: PropTypes.string,
      secondary: PropTypes.string,
      tertiary: PropTypes.string,
    }),
    inputs: PropTypes.object,
    results: PropTypes.object,
  }).isRequired,
};

export default DealStructureSnapshotExpertView;
